import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Card,
  Label,
  MonoMeta,
  StateWord,
  WaitingChip,
  SummaryStripBase,
  DetailOverlay,
  ItemEditorBar,
  NoteOnCard,
} from "@/design/primitives";

const SEPARATOR = " · ";

const TONE = {
  chip: "amber",
  amber: "amber",
  green: "green",
  red: "red",
};

const STATE_LOOKUP = {
  waiting: { word: "Waiting on you", tone: "chip" },
  attention: { word: "Needs attention", tone: "amber" },
  allclear: { word: "All clear", tone: "green" },
};

const AsOfStamp = ({ asOf, hours, fresh, dead }) => (
  <MonoMeta
    as="p"
    data-testid="email-as-of"
    data-fresh={fresh}
    data-dead={dead}
    className={cn(
      "shrink-0 whitespace-nowrap pt-1",
      dead ? "text-os-red" : fresh ? "text-os-ink" : "text-os-amber",
    )}
  >
    count from {asOf}
    {dead ? " — board stale" : fresh ? "" : " — feed behind"}
  </MonoMeta>
);

const BigNumber = ({ number, label }) => (
  <div data-testid="email-big-number">
    <div className="text-[2.875rem] font-semibold leading-none tracking-[-0.035em] text-os-ink tabular-nums">{number}</div>
    <Label as="div" className="mt-2">{label}</Label>
  </div>
);

const CountRow = ({ value, label, tone = "ink" }) => (
  <div className="flex items-baseline gap-2">
    <span className={cn("text-[1rem] font-semibold tabular-nums", tone === "red" ? "text-os-red" : tone === "amber" ? "text-os-amber" : "text-os-ink")}>
      {value}
    </span>
    <span className="text-[0.8125rem] text-os-ink-2">{label}</span>
  </div>
);

const EmailSummary = ({ meta, items, variant }) => {
  const clean = meta?.clean_total ?? items.reduce((s, c) => s + (c.payload?.clean || 0), 0);
  const longer = meta?.waiting_longer_total ?? items.reduce((s, c) => s + (c.payload?.waitingLonger || 0), 0);
  const old = meta?.old_total ?? items.reduce((s, c) => s + (c.payload?.old || 0), 0);
  const dead = meta?.dead;
  const fresh = meta?.fresh !== false;

  const parts = [];
  if (clean > 0) parts.push({ key: "clean", text: `${clean} need a reply`, tone: "red" });
  else parts.push({ key: "clean", text: "0 need a reply", tone: "green" });
  if (longer > 0) parts.push({ key: "longer", text: `${longer} waiting longer`, tone: "amber" });
  if (old > 0) parts.push({ key: "old", text: `${old} left parked`, tone: "red" });
  if (clean === 0 && longer === 0 && old === 0) parts.push({ key: "zero", text: "inbox zero", tone: "green" });

  const tone = dead ? "red" : !fresh ? "amber" : clean > 0 ? "red" : longer > 0 || old > 0 ? "amber" : "quiet";

  return (
    <SummaryStripBase
      testId="email-summary"
      parts={parts}
      tone={tone}
      text={parts.map((p) => p.text).join(SEPARATOR)}
      separator={SEPARATOR}
      size={variant === "desktop" ? "lg" : "sm"}
      meta={meta?.as_of ? <AsOfStamp asOf={meta.as_of} hours={meta.hours} fresh={fresh} dead={dead} /> : null}
    />
  );
};

const Headline = ({ meta, items }) => {
  const clean = meta?.clean_total ?? items.reduce((s, c) => s + (c.payload?.clean || 0), 0);
  const longer = meta?.waiting_longer_total ?? items.reduce((s, c) => s + (c.payload?.waitingLonger || 0), 0);
  const old = meta?.old_total ?? items.reduce((s, c) => s + (c.payload?.old || 0), 0);

  if (clean === 0 && longer === 0 && old === 0) {
    return (
      <Card quiet className="px-5 py-3">
        <p className="text-[1.125rem] font-medium text-os-green">Inbox zero — nothing waiting on you</p>
      </Card>
    );
  }
  if (clean === 0 && (longer > 0 || old > 0)) {
    return (
      <Card quiet className="border-os-red px-5 py-3">
        <p className="text-[1.125rem] font-medium text-os-ink">Nothing new waiting on you</p>
        <p className="mt-1 text-[0.875rem] text-os-red">{longer + old} are still open and older than 30 days</p>
      </Card>
    );
  }
  return null;
};

const FreshnessBanner = ({ meta }) => {
  if (!meta || meta.fresh) return null;
  if (meta.dead) {
    return (
      <Card quiet className="border-os-red px-5 py-3">
        <p className="text-[0.875rem] text-os-red">Board stale — not checking right now. Kit needs to look at the feed.</p>
      </Card>
    );
  }
  return (
    <Card quiet className="border-os-amber px-5 py-3">
      <p className="text-[0.875rem] text-os-amber">Feed is behind. Numbers are real, just not the latest pull.</p>
    </Card>
  );
};

const EmailCard = ({ item, onOpen }) => {
  const p = item.payload || {};
  const { state, stateWord, stateTone, stateLine, title, asOf, clean, waitingLonger, old, answered, bounced, oldestDays, cameIn7d, answered7d, latest, burstNote, waitingLongerLine, replyLag } = p;
  const tone = TONE[stateTone] || "amber";
  const interactive = typeof onOpen === "function";
  const open = interactive ? () => onOpen(item.slug) : undefined;

  const cardProps = interactive
    ? {
        role: "button",
        tabIndex: 0,
        onClick: open,
        onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), open()),
        className: "flex h-full cursor-pointer flex-col p-5 text-left hover:border-os-border-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink",
      }
    : {
        className: "flex h-full flex-col p-5 text-left",
      };

  return (
    <Card
      quiet={state === "allclear"}
      data-testid={`email-tile-${item.slug}`}
      data-state={state}
      aria-label={`${title}, ${stateWord}.${interactive ? " Open details" : ""}`}
      {...cardProps}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">{title}</h3>
        <StateWord tone={tone}>{stateWord}</StateWord>
      </div>
      <p className="mt-3 text-[0.875rem] leading-[1.45] text-os-ink-2">{stateLine}</p>
      {burstNote && (
        <p data-testid={`email-tile-burst-${item.slug}`} className="mt-2 text-[0.8125rem] leading-[1.4] text-os-ink-3">
          {burstNote}
        </p>
      )}
      {waitingLongerLine && (
        <p data-testid={`email-tile-longer-${item.slug}`} className="mt-2 text-[0.8125rem] leading-[1.4] text-os-red">
          {waitingLongerLine}
        </p>
      )}
      <div data-testid="email-tile-measure" className="mt-4 border-t border-os-border pt-4">
        <BigNumber number={clean} label={clean === 1 ? "needs a reply" : "need a reply"} />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <CountRow value={waitingLonger} label="waiting longer" tone="amber" />
          <CountRow value={answered} label="answered" tone="ink" />
          <CountRow value={bounced} label="couldn't deliver" tone={bounced > 0 ? "red" : "ink"} />
          <CountRow value={cameIn7d} label="came in this week" tone="ink" />
        </div>
        {replyLag?.avgDays > 0 && (
          <p className="mt-3 text-[0.8125rem] text-os-ink-3">
            Reply lag: {replyLag.avgDays} day average · oldest {replyLag.maxDays} days
          </p>
        )}
      </div>
      {latest && (
        <div className="mt-3 border-t border-os-border pt-3">
          <p className="text-[0.8125rem] leading-[1.45] text-os-ink">
            Latest: <span className="text-os-ink-2">{latest.subject}</span>
          </p>
          <p className="mt-1 text-[0.75rem] text-os-ink-3">
            {latest.contact} · {latest.cameIn} · {latest.whatKitDid}
          </p>
        </div>
      )}
      <MonoMeta as="p" data-testid={`email-tile-stamp-${item.slug}`} className="mt-3">
        current as of {asOf}
      </MonoMeta>
    </Card>
  );
};

const EmailCardDetail = ({ item, board, variant, onClose }) => {
  const p = item.payload || {};
  const { title, stateWord, stateTone, stateLine, clean, waitingLonger, old, answered, bounced, oldestDays, avgReplyLagDays, cameIn7d, answered7d, latest, burstNote, waitingLongerLine, replyLag, asOf } = p;
  const tone = TONE[stateTone] || "amber";

  return (
    <DetailOverlay variant={variant === "desktop" ? "panel" : "sheet"} onClose={onClose} label={`${title} details`} testId="email-detail">
      <div className="pr-12 pt-2">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="min-w-0 text-[1.25rem] font-semibold tracking-[-0.02em] text-os-ink">{title}</h2>
          <StateWord tone={tone}>{stateWord}</StateWord>
        </div>
        <p className="mt-2 text-[0.9375rem] leading-[1.5] text-os-ink-2">{stateLine}</p>
      </div>
      {clean > 0 && (
        <div className="mt-5">
          <WaitingChip>{clean} {clean === 1 ? "item needs" : "items need"} a reply or call-back</WaitingChip>
        </div>
      )}
      <div className="mt-6 border-t border-os-border pt-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <CountRow value={clean} label="need a reply" tone="red" />
          <CountRow value={waitingLonger} label="waiting longer" tone="amber" />
          <CountRow value={old} label="left parked" tone="red" />
          <CountRow value={answered} label="answered" tone="ink" />
          <CountRow value={bounced} label="couldn't deliver" tone={bounced > 0 ? "red" : "ink"} />
          <CountRow value={cameIn7d} label="came in this week" tone="ink" />
        </div>
        {replyLag?.avgDays > 0 && (
          <p className="mt-4 text-[0.875rem] text-os-ink-2">
            Reply lag: {replyLag.avgDays} day average · oldest open {replyLag.maxDays} days
          </p>
        )}
        {latest && (
          <div className="mt-4 border-t border-os-border pt-4">
            <Label as="h3">Most recent</Label>
            <p className="mt-1.5 text-[0.9375rem] leading-[1.55] text-os-ink">{latest.subject}</p>
            <p className="mt-1 text-[0.8125rem] text-os-ink-3">{latest.contact} · {latest.cameIn} · {latest.whatKitDid}</p>
          </div>
        )}
        {burstNote && (
          <p className="mt-4 text-[0.875rem] leading-[1.5] text-os-ink-3">{burstNote}</p>
        )}
        {waitingLongerLine && (
          <p className="mt-4 text-[0.875rem] leading-[1.5] text-os-red">{waitingLongerLine}</p>
        )}
      </div>
      <p className="mt-4 text-[0.8125rem] text-os-ink-3">Current as of {asOf}</p>
      {board?.live && (
        <div className="mt-4 border-t border-os-border pt-4">
          <NoteOnCard board={board} slug={item.slug} testId={`email-note-${item.slug}`} />
        </div>
      )}
      <ItemEditorBar board={board} item={item} onClose={onClose} testId={`email-editor-${item.slug}`} />
    </DetailOverlay>
  );
};

export const EmailBoard = ({ items, meta, variant = "desktop", board }) => {
  const [openSlug, setOpenSlug] = useState(null);
  const desktop = variant === "desktop";
  const openItem = items.find((i) => i.slug === openSlug);
  const close = useCallback(() => setOpenSlug(null), []);

  return (
    <div data-testid="email-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <EmailSummary meta={meta} items={items} variant={variant} />
        <Headline meta={meta} items={items} />
        <FreshnessBanner meta={meta} />
        <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
          {items.map((item) => (
            <EmailCard key={item.slug} item={item} onOpen={board?.live ? setOpenSlug : undefined} />
          ))}
        </div>
      </div>
      {openItem && board?.live && <EmailCardDetail item={openItem} board={board} variant={variant} onClose={close} />}
    </div>
  );
};

export default EmailBoard;
